/**
 * 通用验证器工具函数
 */

/**
 * 密码强度级别
 */
export type PasswordStrength = 'weak' | 'medium' | 'strong';

/**
 * 密码强度检测结果
 */
export interface PasswordStrengthResult {
  /** 强度级别 */
  strength: PasswordStrength;
  /** 强度分数 (0-100) */
  score: number;
  /** 建议列表 */
  suggestions: string[];
}

/**
 * 密码强度建议消息（可自定义以支持国际化）
 */
export interface PasswordMessages {
  empty: string;
  minLength: string;
  lowercase: string;
  uppercase: string;
  digit: string;
  special: string;
}

/** 默认密码强度建议（英文） */
const DEFAULT_PASSWORD_MESSAGES: PasswordMessages = {
  empty: 'Please enter a password',
  minLength: 'Password must be at least 8 characters',
  lowercase: 'Should contain lowercase letters',
  uppercase: 'Should contain uppercase letters',
  digit: 'Should contain digits',
  special: 'Should contain special characters',
};

/**
 * 文件验证选项
 */
export interface FileValidateOptions {
  /** 允许的文件类型 (MIME 类型或扩展名) */
  allowedTypes?: string[];
  /** 最大文件大小 (字节) */
  maxSize?: number;
}

/**
 * 验证邮箱格式
 *
 * @param email - 邮箱地址
 * @returns 是否有效
 */
export function validateEmail(email: string): boolean {
  if (!email) return false;
  const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return pattern.test(email.trim());
}

/**
 * 验证中国手机号格式
 *
 * @param phone - 手机号
 * @returns 是否有效
 */
export function validatePhone(phone: string): boolean {
  if (!phone) return false;
  const pattern = /^1[3-9]\d{9}$/;
  return pattern.test(phone.trim());
}

/**
 * 验证 URL 格式
 *
 * @param url - URL 字符串
 * @returns 是否有效
 */
export function validateURL(url: string): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url.trim());
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}

/**
 * 检测密码强度
 *
 * @param password - 密码
 * @param messages - 自定义建议消息（可选，用于国际化）
 * @returns 密码强度结果
 *
 * @example
 * ```ts
 * const result = validatePasswordStrength('Abc123!@#');
 * // { strength: 'strong', score: 85, suggestions: [] }
 *
 * // 自定义中文消息
 * const result = validatePasswordStrength('abc', {
 *   empty: '请输入密码',
 *   minLength: '密码长度至少8位',
 *   lowercase: '建议包含小写字母',
 *   uppercase: '建议包含大写字母',
 *   digit: '建议包含数字',
 *   special: '建议包含特殊字符',
 * });
 * ```
 */
export function validatePasswordStrength(
  password: string,
  messages?: Partial<PasswordMessages>,
): PasswordStrengthResult {
  const msg = { ...DEFAULT_PASSWORD_MESSAGES, ...messages };
  const suggestions: string[] = [];
  let score = 0;

  if (!password) {
    return { strength: 'weak', score: 0, suggestions: [msg.empty] };
  }

  // 长度评分
  if (password.length >= 8) score += 20;
  else suggestions.push(msg.minLength);

  if (password.length >= 12) score += 10;
  if (password.length >= 16) score += 5;

  // 包含小写字母
  if (/[a-z]/.test(password)) score += 15;
  else suggestions.push(msg.lowercase);

  // 包含大写字母
  if (/[A-Z]/.test(password)) score += 15;
  else suggestions.push(msg.uppercase);

  // 包含数字
  if (/\d/.test(password)) score += 15;
  else suggestions.push(msg.digit);

  // 包含特殊字符
  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) score += 20;
  else suggestions.push(msg.special);

  // 确定强度级别
  let strength: PasswordStrength;
  if (score >= 70) {
    strength = 'strong';
  } else if (score >= 40) {
    strength = 'medium';
  } else {
    strength = 'weak';
  }

  return { strength, score: Math.min(score, 100), suggestions };
}

/**
 * 验证中国身份证号
 *
 * @param idCard - 身份证号
 * @returns 是否有效
 */
export function validateIdCard(idCard: string): boolean {
  if (!idCard) return false;
  const str = idCard.trim();

  // 18位身份证
  if (str.length === 18) {
    const pattern = /^\d{17}[\dXx]$/;
    if (!pattern.test(str)) return false;

    // 校验位验证
    const weights = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
    const checkCodes = ['1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2'];
    let sum = 0;
    for (let i = 0; i < 17; i++) {
      sum += Number.parseInt(str[i], 10) * weights[i];
    }
    const checkCode = checkCodes[sum % 11];
    return str[17].toUpperCase() === checkCode;
  }

  // 15位身份证
  if (str.length === 15) {
    return /^\d{15}$/.test(str);
  }

  return false;
}

/**
 * 验证银行卡号 (Luhn 算法)
 *
 * @param cardNo - 银行卡号
 * @returns 是否有效
 */
export function validateBankCard(cardNo: string): boolean {
  if (!cardNo) return false;
  const str = cardNo.trim().replace(/\s/g, '');
  if (!/^\d{13,19}$/.test(str)) return false;

  // Luhn 算法
  let sum = 0;
  let isEven = false;
  for (let i = str.length - 1; i >= 0; i--) {
    let digit = Number.parseInt(str[i], 10);
    if (isEven) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    isEven = !isEven;
  }
  return sum % 10 === 0;
}

/**
 * 验证文件类型
 *
 * @param file - 文件对象
 * @param allowedTypes - 允许的类型 (MIME 类型或扩展名，如 ['.jpg', '.png', 'image/jpeg'])
 * @returns 是否有效
 */
export function validateFileType(
  file: { name: string; type?: string },
  allowedTypes: string[],
): boolean {
  if (!file || !allowedTypes.length) return true;

  const fileName = file.name.toLowerCase();
  const fileType = file.type?.toLowerCase() || '';

  return allowedTypes.some((type) => {
    const t = type.toLowerCase();
    if (t.startsWith('.')) {
      // 扩展名匹配
      return fileName.endsWith(t);
    }
    // MIME 类型匹配
    return fileType === t || fileType.startsWith(`${t.split('/')[0]}/`);
  });
}

/**
 * 验证文件大小
 *
 * @param file - 文件对象
 * @param maxSize - 最大大小 (字节)
 * @returns 是否在限制内
 */
export function validateFileSize(
  file: { size: number },
  maxSize: number,
): boolean {
  if (!file || maxSize <= 0) return true;
  return file.size <= maxSize;
}

/**
 * 验证数字范围
 *
 * @param value - 数值
 * @param min - 最小值 (含)
 * @param max - 最大值 (含)
 * @returns 是否在范围内
 */
export function validateNumberRange(
  value: number,
  min: number,
  max: number,
): boolean {
  return value >= min && value <= max;
}
